import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, } from 'react';
import dayjs from 'dayjs';
import DayjsUtils from '@date-io/dayjs';
import { MuiPickersUtilsProvider, useUtils as useMuiUtils, } from '@material-ui/pickers';
import { findClosestEnabledDate as MuiFindClosestEnabledDate } from '@material-ui/pickers/_helpers/date-utils';
import { DateBorder } from '@ringcentral/juno-icon';
import { combineClasses, combineProps, omit, styled, useControlled, useDeprecatedCheck, useEventCallback, useThemeProps, } from '../../../../foundation';
import { PickerTextField, } from '../utils';
import { Calendar } from './Calendar';
import { invalidateDateInRange, RcDatePickerClasses, ScreenReaderProvider, } from './utils';
var defaultMinDate = new Date('1900-01-01');
var defaultMaxDate = new Date('2100-01-01');
var DEFAULT_FORMAT = 'MM/DD/YYYY';
/** @release */
var InnerRcDatePicker = forwardRef(function (props, ref) {
    var date = props.date, _a = props.value, value = _a === void 0 ? date : _a, onChange = props.onChange, formatString = props.formatString, locale = props.locale, todayButtonText = props.todayButtonText, minDateProp = props.minDate, maxDateProp = props.maxDate, min = props.min, max = props.max, disableFuture = props.disableFuture, disablePast = props.disablePast, loadingIndicator = props.loadingIndicator, onMonthChange = props.onMonthChange, renderDay = props.renderDay, disabled = props.disabled, shouldDisableDateProp = props.shouldDisableDate, size = props.size, onClose = props.onClose, onClear = props.onClear, classes = props.classes, PopoverPropsProp = props.PopoverProps, screenReaderProps = props.screenReaderProps, rest = __rest(props, ["date", "value", "onChange", "formatString", "locale", "todayButtonText", "minDate", "maxDate", "min", "max", "disableFuture", "disablePast", "loadingIndicator", "onMonthChange", "renderDay", "disabled", "shouldDisableDate", "size", "onClose", "onClear", "classes", "PopoverProps", "screenReaderProps"]);
    var utils = useMuiUtils();
    var _b = __read(useControlled({
        controlled: value,
        default: null,
        name: 'RcDatePicker',
    }), 2), controlledValue = _b[0], setControlledValue = _b[1];
    var emitValueRef = useRef(null);
    var maxDate = minDateProp || max;
    var minDate = maxDateProp || min;
    var actionRef = useRef(null);
    var dateRange = useMemo(function () { return ({
        min: dayjs(minDate || defaultMinDate),
        max: dayjs(maxDate || defaultMaxDate),
    }); }, [minDate, maxDate]);
    var getInvalidateDateInRange = useEventCallback(function (day) {
        return invalidateDateInRange(day, { dateRange: dateRange, now: utils.date(), disableFuture: disableFuture, disablePast: disablePast }, utils);
    });
    var shouldDisableDate = useEventCallback(function (day) {
        return (Boolean(getInvalidateDateInRange(day)) ||
            Boolean(shouldDisableDateProp === null || shouldDisableDateProp === void 0 ? void 0 : shouldDisableDateProp(day)));
    });
    var getClosestEnableDate = useCallback(function (currDate) {
        return MuiFindClosestEnabledDate({
            date: currDate,
            utils: utils,
            minDate: utils.date(dateRange.min),
            maxDate: utils.date(dateRange.max),
            disablePast: Boolean(disablePast),
            disableFuture: Boolean(disableFuture),
            shouldDisableDate: shouldDisableDate,
        });
    }, [
        dateRange.max,
        dateRange.min,
        disableFuture,
        disablePast,
        shouldDisableDate,
        utils,
    ]);
    var initDate = useMemo(function () {
        return getClosestEnableDate(dayjs(utils.date()));
    }, [getClosestEnableDate, utils]);
    var dayjsValue = useMemo(function () { return (controlledValue ? dayjs(controlledValue) : null); }, [controlledValue]);
    var nowDate = dayjsValue || initDate;
    var textFiledValue = useMemo(function () { return (dayjsValue ? utils.format(dayjsValue, formatString) : ''); }, [dayjsValue, utils, formatString]);
    var PopoverProps = useMemo(function () {
        return combineProps({
            classes: combineClasses({
                root: RcDatePickerClasses.popover,
                paper: RcDatePickerClasses.popoverPaper,
            }, {
                root: classes === null || classes === void 0 ? void 0 : classes.popover,
                paper: classes === null || classes === void 0 ? void 0 : classes.popoverPaper,
            }),
            onClose: onClose,
        }, PopoverPropsProp);
    }, [PopoverPropsProp, classes, onClose]);
    var calendarClasses = useMemo(function () {
        return combineClasses(omit(RcDatePickerClasses, ['popover', 'popoverPaper']), omit(classes, ['popover', 'popoverPaper']));
    }, [classes]);
    var handleChange = function (toDate, fromUserSelect) {
        var _a;
        (_a = actionRef.current) === null || _a === void 0 ? void 0 : _a.close();
        setControlledValue(toDate);
        // save inner change value
        emitValueRef.current = toDate;
        onChange === null || onChange === void 0 ? void 0 : onChange(toDate, fromUserSelect);
    };
    var handleClear = useEventCallback(function (evt) {
        onClear === null || onClear === void 0 ? void 0 : onClear(evt);
        handleChange === null || handleChange === void 0 ? void 0 : handleChange(null);
    });
    var handleDaySelect = function (day, fromUserSelect) {
        if (fromUserSelect === void 0) { fromUserSelect = true; }
        var newDay = day ? utils.startOfDay(day).toDate() : null;
        handleChange(newDay, fromUserSelect);
    };
    useEffect(function () {
        if (
        // only when inner value change need check again is that value is valid
        dayjsValue &&
            value !== emitValueRef.current &&
            shouldDisableDate(dayjsValue)) {
            var closestEnabledDate = getClosestEnableDate(dayjsValue);
            handleDaySelect(closestEnabledDate, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (React.createElement(PickerTextField, __assign({ ref: ref, action: actionRef, onClear: handleClear, PopoverProps: PopoverProps, value: textFiledValue, disabled: disabled, ActionSymbol: DateBorder }, rest),
        React.createElement(ScreenReaderProvider, { screenReaderProps: screenReaderProps },
            React.createElement(Calendar, { size: size, classes: calendarClasses, date: nowDate, dateRange: dateRange, handleDaySelect: handleDaySelect, disableFuture: disableFuture, disablePast: disablePast, loadingIndicator: loadingIndicator, onMonthChange: onMonthChange, renderDay: renderDay, shouldDisableDate: shouldDisableDate, getInvalidateDateInRange: getInvalidateDateInRange, todayButtonText: todayButtonText, formatString: formatString }))));
});
var _RcDatePicker = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcDatePicker' });
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDeprecatedCheck(RcDatePicker, props, [
            {
                prop: 'minDate',
                time: '2021-3',
                comment: "Min date @DateIOType, using min instead ",
            },
            {
                prop: 'maxDate',
                time: '2021-3',
                comment: "Max date @DateIOType, using max instead ",
            },
            {
                prop: 'date',
                time: '2021-3',
                comment: "please use value to replace that ",
            },
        ]);
    }
    return (React.createElement(MuiPickersUtilsProvider, { utils: DayjsUtils, locale: props.locale, libInstance: dayjs },
        React.createElement(InnerRcDatePicker, __assign({ ref: ref }, props))));
});
var RcDatePicker = styled(_RcDatePicker)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
RcDatePicker.defaultProps = {
    clearBtn: true,
    formatString: DEFAULT_FORMAT,
    size: 'medium',
    todayButtonText: 'Today',
    locale: 'en',
    label: 'Date',
};
RcDatePicker.displayName = 'RcDatePicker';
export { DEFAULT_FORMAT, RcDatePicker, RcDatePickerClasses };
var templateObject_1;
